import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useQuery } from 'react-query';
import api from '../services/api';
import StudentSearchSelect from './StudentSearchSelect';
import './AttendanceForm.css';

const AttendanceForm = ({ initialData, onSubmit, onCancel, isLoading }) => {
  const { register, handleSubmit, watch, setValue, formState: { errors } } = useForm({
    defaultValues: initialData || {
      student_id: '',
      enrollment_id: '',
      group_id: '',
      attendance_date: new Date().toISOString().slice(0, 16),
      status: 'present',
      mark_method: 'manual',
      notes: '',
    },
  });

  const selectedStudentId = watch('student_id');
  const selectedGroupId = watch('group_id');
  const safeStudentId = selectedStudentId && !isNaN(selectedStudentId) ? String(selectedStudentId) : '';

  const { data: selectedStudent } = useQuery(
    ['student', safeStudentId],
    async () => {
      const response = await api.get(`/students/${safeStudentId}`);
      return response.data;
    },
    { enabled: !!safeStudentId }
  );

  // Obtener grupos
  const { data: groups } = useQuery('groups', async () => {
    const response = await api.get('/groups', { params: { is_active: true } });
    return response.data;
  });

  // Obtener matrículas activas del estudiante seleccionado
  const { data: enrollments } = useQuery(
    ['enrollments', safeStudentId],
    async () => {
      if (!safeStudentId) return [];
      const response = await api.get('/enrollments', {
        params: { student_id: safeStudentId, status: 'active' }
      });
      return response.data;
    },
    { enabled: !!safeStudentId }
  );

  // Obtener grupos únicos donde el estudiante tiene matrícula activa
  const studentGroups = React.useMemo(() => {
    if (!enrollments || enrollments.length === 0) return [];
    const groupIds = [...new Set(enrollments.map(e => e.group_id))];
    return groups?.filter(g => groupIds.includes(g.id)) || [];
  }, [enrollments, groups]);

  // Filtrar matrículas del grupo seleccionado
  const filteredEnrollments = enrollments?.filter(
    enrollment => !selectedGroupId || enrollment.group_id === parseInt(selectedGroupId)
  );

  useEffect(() => {
    if (initialData) {
      // Si estamos editando, establecer valores
      if (initialData.attendance_date) {
        const date = new Date(initialData.attendance_date);
        setValue('attendance_date', date.toISOString().slice(0, 16));
      }
    }
  }, [initialData, setValue]);

  // Auto-seleccionar matrícula si solo hay una
  useEffect(() => {
    if (filteredEnrollments && filteredEnrollments.length === 1 && !initialData) {
      setValue('enrollment_id', filteredEnrollments[0].id);
    }
  }, [filteredEnrollments, setValue, initialData]);

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="attendance-form">
      <div className="form-group">
        <label htmlFor="student_id">Estudiante *</label>
        {initialData ? (
          <input
            type="text"
            value={selectedStudent ? `${selectedStudent.first_name} ${selectedStudent.last_name}` : `ID: ${initialData.student_id}`}
            disabled
          />
        ) : (
          <>
            <StudentSearchSelect
              value={safeStudentId}
              hasError={!!errors.student_id}
              onChange={(studentId) => {
                setValue('student_id', studentId || '');
                setValue('group_id', '');
                setValue('enrollment_id', '');
              }}
              hiddenInputProps={register('student_id', {
                required: 'El estudiante es obligatorio',
              })}
            />
          </>
        )}
        {errors.student_id && <span className="error-message">{errors.student_id.message}</span>}
        {initialData && (
          <span className="info-message">
            ℹ️ No se puede cambiar el estudiante de una asistencia existente
          </span>
        )}
        {!!safeStudentId && enrollments && enrollments.length === 0 && (
          <span className="warning-message">
            ⚠️ Este estudiante no tiene matrículas activas
          </span>
        )}
      </div>

      <div className="form-group">
        <label htmlFor="group_id">Grupo *</label>
        <select
          id="group_id"
          {...register('group_id', { 
            required: 'El grupo es obligatorio'
          })}
          className={errors.group_id ? 'input-error' : ''}
          disabled={!safeStudentId || studentGroups.length === 0 || !!initialData}
        >
          <option value="">
            {!selectedStudentId 
              ? 'Selecciona primero un estudiante'
              : studentGroups.length === 0
              ? 'El estudiante no tiene grupos activos'
              : 'Seleccionar grupo...'}
          </option>
          {studentGroups?.map((group) => (
            <option key={group.id} value={group.id}>
              {group.discipline?.icon || '⚽'} {group.name} - {group.level?.name || 'Nivel'}
            </option>
          ))}
        </select>
        {errors.group_id && <span className="error-message">{errors.group_id.message}</span>}
        {initialData && (
          <span className="info-message">
            ℹ️ No se puede cambiar el grupo de una asistencia existente
          </span>
        )}
        {!!safeStudentId && studentGroups.length > 0 && (
          <span className="success-message">
            ✅ {studentGroups.length} grupo{studentGroups.length > 1 ? 's' : ''} disponible{studentGroups.length > 1 ? 's' : ''}
          </span>
        )}
      </div>

      <div className="form-group">
        <label htmlFor="enrollment_id">Matrícula *</label>
        <select
          id="enrollment_id"
          {...register('enrollment_id', { 
            required: 'La matrícula es obligatoria'
          })}
          className={errors.enrollment_id ? 'input-error' : ''}
          disabled={!safeStudentId || !filteredEnrollments || filteredEnrollments.length === 0 || !!initialData}
        >
          <option value="">
            {!safeStudentId 
              ? 'Selecciona primero un estudiante'
              : !filteredEnrollments || filteredEnrollments.length === 0
              ? 'No hay matrículas activas para este estudiante en este grupo'
              : 'Seleccionar matrícula...'}
          </option>
          {filteredEnrollments?.map((enrollment) => (
            <option key={enrollment.id} value={enrollment.id}>
              Matrícula #{enrollment.id} - {formatDate(enrollment.start_date)} a {formatDate(enrollment.end_date) || 'Activa'}
            </option>
          ))}
        </select>
        {errors.enrollment_id && <span className="error-message">{errors.enrollment_id.message}</span>}
        {initialData && (
          <span className="info-message">
            ℹ️ No se puede cambiar la matrícula de una asistencia existente
          </span>
        )}
      </div>

      <div className="form-group">
        <label htmlFor="attendance_date">Fecha y Hora *</label>
        <input
          type="datetime-local"
          id="attendance_date"
          {...register('attendance_date', { required: 'La fecha y hora son obligatorias' })}
          className={errors.attendance_date ? 'input-error' : ''}
          disabled={!!initialData}
        />
        {errors.attendance_date && <span className="error-message">{errors.attendance_date.message}</span>}
        {initialData && (
          <span className="info-message">
            ℹ️ No se puede cambiar la fecha de una asistencia existente
          </span>
        )}
      </div>

      <div className="form-group">
        <label htmlFor="status">Estado *</label>
        <select
          id="status"
          {...register('status', { required: 'El estado es obligatorio' })}
          className={errors.status ? 'input-error' : ''}
        >
          <option value="present">✅ Presente</option>
          <option value="absent">❌ Ausente</option>
          <option value="late">⏰ Tarde</option>
          <option value="excused">📝 Justificado</option>
        </select>
        {errors.status && <span className="error-message">{errors.status.message}</span>}
      </div>

      <div className="form-group">
        <label htmlFor="mark_method">Método de Marcado</label>
        <select
          id="mark_method"
          {...register('mark_method')}
        >
          <option value="manual">✋ Manual</option>
          <option value="qr_scan">📱 Escaneo QR</option>
        </select>
      </div>

      <div className="form-group">
        <label htmlFor="notes">Notas</label>
        <textarea
          id="notes"
          {...register('notes')}
          rows="3"
          placeholder="Notas adicionales sobre la asistencia..."
        />
      </div>

      <div className="modal-actions">
        <button type="button" className="btn btn-cancel" onClick={onCancel} disabled={isLoading}>
          Cancelar
        </button>
        <button type="submit" className="btn btn-primary" disabled={isLoading}>
          {isLoading ? 'Guardando...' : initialData ? 'Actualizar' : 'Marcar'}
        </button>
      </div>
    </form>
  );

  function formatDate(dateString) {
    if (!dateString) return null;
    const date = new Date(dateString);
    return date.toLocaleDateString('es-ES', { year: 'numeric', month: '2-digit', day: '2-digit' });
  }
};

export default AttendanceForm;

